/**
 * App Message API Service
 *
 * API methods for application messages sent by tenant applications.
 * Communicates with the Runtime API.
 */

import runtimeApiClient from './runtime-api-client';
import type { ApiSuccessResponse } from '../types/api.types';
import type {
  AppMessageSanitized,
  AppMessageListQuery,
  AppMessageListResponse,
  AppMessageUnreadCountResponse,
  AppMessageMarkAllAsReadResponse,
} from '../types/app-message.types';
import type { BulkActionType, BulkActionResult } from '../types/inbox.types';

// ============================================================================
// APP MESSAGE SERVICE
// ============================================================================

export const appMessageService = {
  /**
   * List app messages for the current user
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param query - Query parameters (unreadOnly, type, limit, cursor)
   */
  listMessages: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    query?: AppMessageListQuery
  ): Promise<AppMessageListResponse> => {
    const response = await runtimeApiClient.get<ApiSuccessResponse<AppMessageListResponse>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages`,
      { params: query }
    );
    return response.data.data;
  },

  /**
   * Get unread message count for badge display
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   */
  getUnreadCount: async (
    tenantId: string,
    applicationId: string,
    userId: string
  ): Promise<number> => {
    const response = await runtimeApiClient.get<ApiSuccessResponse<AppMessageUnreadCountResponse>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/unread-count`
    );
    return response.data.data.count;
  },

  /**
   * Mark a single message as read
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   */
  markAsRead: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<ApiSuccessResponse<AppMessageSanitized>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/read`
    );
    return response.data.data;
  },

  /**
   * Mark all messages as read
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   */
  markAllAsRead: async (
    tenantId: string,
    applicationId: string,
    userId: string
  ): Promise<number> => {
    const response = await runtimeApiClient.post<ApiSuccessResponse<AppMessageMarkAllAsReadResponse>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/read-all`
    );
    return response.data.data.count;
  },

  /**
   * Archive a message
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   */
  archiveMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<ApiSuccessResponse<AppMessageSanitized>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/archive`
    );
    return response.data.data;
  },

  /**
   * Unarchive a message (move back to inbox)
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   */
  unarchiveMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<ApiSuccessResponse<AppMessageSanitized>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/unarchive`
    );
    return response.data.data;
  },

  /**
   * Soft delete a message (moves to deleted view with 30-day TTL)
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   */
  softDeleteMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<ApiSuccessResponse<AppMessageSanitized>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/delete`
    );
    return response.data.data;
  },

  /**
   * Restore a soft-deleted message (moves back to inbox)
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   */
  restoreMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<AppMessageSanitized> => {
    const response = await runtimeApiClient.post<ApiSuccessResponse<AppMessageSanitized>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}/restore`
    );
    return response.data.data;
  },

  /**
   * Permanently delete a message (only works on soft-deleted messages)
   * @param tenantId - Tenant UUID
   * @param applicationId - Application UUID
   * @param userId - User UUID
   * @param messageId - Message UUID
   */
  permanentDeleteMessage: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    messageId: string
  ): Promise<void> => {
    await runtimeApiClient.delete<ApiSuccessResponse<{ message: string }>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/${messageId}`
    );
  },

  /**
   * Perform a bulk action on multiple messages within a single application
   */
  bulkAction: async (
    tenantId: string,
    applicationId: string,
    userId: string,
    action: BulkActionType,
    messageIds: string[]
  ): Promise<BulkActionResult> => {
    const response = await runtimeApiClient.post<ApiSuccessResponse<BulkActionResult>>(
      `/tenants/${tenantId}/apps/${applicationId}/users/${userId}/messages/bulk`,
      { action, messageIds }
    );
    return response.data.data;
  },

  /**
   * Perform a bulk action on messages across multiple applications
   */
  bulkActionCrossApp: async (
    tenantId: string,
    userId: string,
    action: BulkActionType,
    items: Array<{ messageId: string; applicationId: string }>
  ): Promise<BulkActionResult> => {
    const response = await runtimeApiClient.post<ApiSuccessResponse<BulkActionResult>>(
      `/tenants/${tenantId}/users/${userId}/messages/bulk`,
      { action, items }
    );
    return response.data.data;
  },
};

export default appMessageService;
